import {
    Component,
    OnInit,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
    ViewChild,
    HostBinding,
    EventEmitter,
    Output,
    Injector,
    OnDestroy,
    SimpleChange,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, NgModel } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DateAdapter } from '@angular/material/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { MatDatepicker } from '@angular/material/datepicker';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isDefined, fiGetLocaleCode } from '@fi-shared/utils';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';
import { isNullOrUndefined } from '../utils/is-null-or-undefined';
import { FiDateAdapter, FI_FORMATS } from './fi-date-adapter';

@Component({
    selector: 'fi-date-picker',
    template: `
        <mat-form-field
            class="input-group"
            [ngClass]="getDatePickerFormFieldClass()"
            [appearance]="appearance"
            [floatLabel]="floatLabel"
        >
            <mat-label>
                @if (label) {
                    {{ label }}
                }
            </mat-label>
            <input
                matInput
                autocomplete="off"
                [matDatepicker]="picker"
                [placeholder]="placeholder"
                [(ngModel)]="model"
                #datePickerModel="ngModel"
                [disabled]="ngDisabled"
                [required]="required"
                [fiCustomValidator]="isInputInvalid"
                [min]="minDate"
                [max]="maxDate"
                name="datepickerModel"
                (dateChange)="onDateChange($event)"
                (focus)="onDatePickerInputFocus()"
            />
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker (opened)="onDatepickerOpened()" (closed)="onDatepickerClosed()"></mat-datepicker>
            <mat-hint>
                @if (hint) {
                    {{ hint }}
                }
            </mat-hint>
            <mat-error
                *ngIf="!suppressError"
                [fiErrorMessage]="datePickerModel"
                [fiErrorCustomMessages]="{
                    matDatepickerMax: matDatepickerMaxErrorTextId,
                    matDatepickerMin: matDatepickerMinErrorTextId,
                    matDatepickerParse: matDatepickerParseErrorTextId,
                    customError: inputErrorTextLabel,
                }"
            >
            </mat-error>
        </mat-form-field>
    `,
    styleUrls: ['./date-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-date-picker' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatePickerComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: DatePickerComponent,
            multi: true,
        },
        { provide: DateAdapter, useClass: FiDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: FI_FORMATS },
    ],
    standalone: false,
})
export class DatePickerComponent
    extends ControlValueAccessorValidatorBase<Date>
    implements OnInit, OnChanges, OnDestroy
{
    @Input() ngDisabled;
    @Input() minDate;
    @Input() maxDate;
    @Input() label: string;
    @Input() appearance: MatFormFieldAppearance = 'fill';
    @Input() placeholder: string;
    @Input() required: boolean;
    @Input() dateMode: boolean;
    @Input() popupMode: boolean;
    @Input() floatLabel: FloatLabelType = 'auto';
    @Input() hint: string;
    @Input() isInputInvalid: boolean;
    @Input() inputErrorTextLabel: string;
    @Input() isStandardFormField: boolean = false;
    @Input() dateFormat: string; // Accept date format as input
    @Input() matDatepickerMaxErrorTextId: string = 'error_message.min_max_date';
    @Input() matDatepickerMinErrorTextId: string = 'error_message.min_max_date';
    @Input() matDatepickerParseErrorTextId: string = 'error_message.wrong_date_format';
    @Input() suppressError: boolean = false;
    @Input() noSubscript: boolean = false;

    @Output() datepickerOpened = new EventEmitter<void>();
    @Output() datepickerClosed = new EventEmitter<void>();

    @ViewChild(MatDatepicker, { static: false }) matDatepickerComponent: MatDatepicker<any>;
    @ViewChild('datePickerModel') datePickerModel: NgModel;
    model: Date;

    constructor(
        private injector: Injector,
        private adapter: DateAdapter<any>,
        private fiUserDataService: FiUserDataService,
    ) {
        super(injector);
        const isoCode = fiGetLocaleCode(this.fiUserDataService.getUserData().ApplicationLanguage);
        adapter.setLocale(isoCode);
        // Set initial custom format if provided
        if (this.dateFormat) {
            (this.adapter as FiDateAdapter).setCustomFormat(this.dateFormat);
        }
    }

    ngOnInit() {
        // Set custom format on init
        if (this.dateFormat) {
            (this.adapter as FiDateAdapter).setCustomFormat(this.dateFormat);
        }
    }

    ngOnDestroy() {
        // Reset custom format on destroy
        (this.adapter as FiDateAdapter).setCustomFormat(null);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.ngModel) {
            const prevModel = this.model;
            this.model = changes.ngModel.currentValue ? this.getModelFromDate(changes.ngModel.currentValue) : null;
            if (!changes.ngModel.firstChange && prevModel !== this.model) {
                this.doPropagateChanges();
            }
        }
        if (changes.minDate) {
            this.minDate = changes.minDate.currentValue ? this.getModelFromDate(changes.minDate.currentValue) : null;
        }
        if (changes.maxDate) {
            this.maxDate = changes.maxDate.currentValue ? this.getModelFromDate(changes.maxDate.currentValue) : null;
        }

        const isSuppressErrorChanged = this.isSuppressErrorChanged(changes.suppressError);
        if ((changes.isInputInvalid && isDefined(this.datePickerModel)) || isSuppressErrorChanged) {
            const tempValue = this.datePickerModel.value;
            // little trick to detect valid state change when isInputInvalid changes in some other place
            // example: AC-4 https://chainformation.atlassian.net/wiki/spaces/FI/pages/2256830920/Operational+period+that+deviates+from+the+checklist+running+period+Completion+view+when+the+checklist+is+not+available
            // todo: find better way

            this.datePickerModel.update.emit();
            setTimeout(() => this.datePickerModel.update.emit(tempValue));
        }
        if (changes.dateFormat) {
            (this.adapter as FiDateAdapter).setCustomFormat(this.dateFormat || null);
        }
    }

    private isSuppressErrorChanged(suppressErrorChange: SimpleChange): boolean {
        if (isNullOrUndefined(suppressErrorChange)) {
            return false;
        }
        return (
            suppressErrorChange &&
            !suppressErrorChange.firstChange &&
            suppressErrorChange.currentValue !== suppressErrorChange.previousValue
        );
    }

    @Output() datePickerInputFocus = new EventEmitter<void>();
    onDatePickerInputFocus() {
        this.datePickerInputFocus.emit();
    }

    @HostBinding('class.popup-mode')
    get isPopupMode() {
        return this.popupMode;
    }

    openDatePicker() {
        this.matDatepickerComponent.open();
    }

    onDatepickerOpened() {
        this.datepickerOpened.next();
        this.onDatePickerInputFocus();
    }
    onDatepickerClosed() {
        this.datepickerClosed.next();
    }

    onDateChange(value: { value: Date }) {
        if (this.dateMode && value.value) {
            this.ngModel = new Date(Date.UTC(value.value.getFullYear(), value.value.getMonth(), value.value.getDate()));
        } else {
            this.ngModel = value.value;
        }

        this.doPropagateChanges();
    }

    private getModelFromDate(v: Date | string) {
        if (!v) {
            return null;
        }
        var date: Date = typeof v === 'string' ? new Date(v) : v;
        return date;
    }

    getDatePickerFormFieldClass(): { [key: string]: boolean } {
        return {
            'suppress-error': this.suppressError,
            'no-subscript': this.noSubscript,
        };
    }
}
